import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router'
import { BrowserRouter as Router, MemoryRouter } from 'react-router-dom'
import { basepath } from './config'
import './app.less'
import './index.css'
import './i18n'
import { ScrollToTop } from '../common/ScrollToTop'
import { AppContainer } from './AppContainer'
import { NavigationDefinition, routeDefinitions } from './navigation/menu'
import { routes as UserRoutes } from '../users/index'
import { routes as CpeRoutes } from '../cpes/index'
import { routes as FlowRoutes } from '../flows/index'
import { routes as IvrRoutes } from '../ivrs/index'
import { routes as HolidayRoutes } from '../schedules/holidays'
import { routes as OpeningHoursRoutes } from '../schedules/openinghours'
import { routes as PagingRoutes } from '../services/pagings/index'
import { routes as CallPickupGroupRoutes } from '../services/callpickupgroups/index'
import { routes as ExecutiveRoutes } from '../services/executiveassistants/index'
import { routes as SupervisorRoutes } from '../services/businessreporting/supervisor'
import { routes as AmccRoutes } from '../services/contactcenter/index'
import { routes as TrunkingSiteRoutes } from '../trunkingsites/index'
import { routes as VoiceCloudSiteRoutes } from '../voicecloudsites/index'
import { routes as AdminManagementRoutes } from '../enterprise/adminmanagement/index'
import { useDispatch, useSelector } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { RootState } from './store'
import { fetchMe, inEnterpriseViewSelector, loadingFailureSelector, loadingSelector, meSelector, selectedSiteSelector, selectSite } from '../authentication/redux'
import { hasUserPrivilege } from '../common/util/privilegeUtils'
import { Spinner } from '../common/components/Spinner'
import { Box, Flex } from 'rebass'
import { QueryClient, QueryClientProvider } from 'react-query'
import SiteSwitcher from './SiteSwitcher'
import { LoginCallback, SecureRoute, Security, useOktaAuth } from '@okta/okta-react'
import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js'
import { api } from '../common/api'
import { Logout } from './Logout'
import { useOktaSession } from '../common/servercall/useOktaSession'
import { UnAuthorized } from './Unauthorized'
import { NotFound } from './NotFound'
import { Container } from '@mui/material'
import ErrorAlert from '../common/components/ErrorAlert'
import { User } from './types'

/** A visual separator entry that can be placed between navigation groups. */
export type SeparatorEntry = { separator: true }

export type NavigationRoute = {
    translationKey?: string
    href: string
    icon?: any
    component: any
    id: string
    parentId?: string
    menuId?: string
    menuParentId?: string
    showInEnterpriseMode?: boolean
    hideInSiteMode?: boolean
    hidden?: (pagingEnabled: boolean, callPickupEnabled: boolean, businessReportingSupervisorEnabled: boolean, advancedContactCenterEnabled: boolean, callCenterCodesEnabled: boolean, me: User | null) => boolean
    /** VpPrivilege required to see this menu item / route. When set, the item is hidden if the user lacks the privilege. */
    requiredPrivilege?: string
}

export type RouteDefinition = NavigationRoute | SeparatorEntry

export const RouteDefinitions: NavigationRoute[] = [
    ...IvrRoutes, ...UserRoutes, ...CpeRoutes, ...FlowRoutes, ...OpeningHoursRoutes, ...HolidayRoutes,
    ...PagingRoutes, ...CallPickupGroupRoutes, ...ExecutiveRoutes, ...SupervisorRoutes, ...AmccRoutes,
    ...TrunkingSiteRoutes, ...VoiceCloudSiteRoutes, ...AdminManagementRoutes, ...NavigationDefinition,
].filter((d): d is NavigationRoute => !('separator' in d))

/**
 * Route wrapper that enforces privilege checks before rendering.
 *
 * If the route (or its parent, resolved via menuId/parentId) declares a `requiredPrivilege`
 * and the current user lacks it, the user is redirected to `/no-permission` instead of
 * rendering the page — preventing them from reaching the API call that would otherwise
 * throw an AccessDeniedException.
 */
const PrivilegedRoute = ({ definition, useOAuth }: { definition: NavigationRoute; useOAuth: boolean; path?: string }) => {
    const me = useSelector(meSelector)
    const site = useSelector(selectedSiteSelector)

    // Resolve effective privilege: own → parent via menuId → parent via parentId
    const parentDefinition = RouteDefinitions.find(d => d.id === (definition.menuId ?? definition.parentId))
    const privilege = definition.requiredPrivilege ?? parentDefinition?.requiredPrivilege
    const siteId = site?.productId ?? undefined

    // Only block once `me` is loaded; during initialisation we let the route through
    const accessDenied = !!me && !!privilege && !hasUserPrivilege(me, privilege, siteId)

    if (accessDenied) {
        return <Route path={definition.href} render={() => <Redirect to='/not-found' />} />
    }

    return useOAuth
        ? <SecureRoute path={definition.href} component={definition.component} />
        : <Route path={definition.href} component={definition.component} />
}

const definitionToRoute = (useOAuth: boolean) => (definition: NavigationRoute) =>
    <PrivilegedRoute path={definition.href} definition={definition} useOAuth={useOAuth} key={definition.id} />

export function useQuery() {
    const {search} = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const oktaCallBackUrl = '/login/callback'

const oktaAuth = new OktaAuth({
    issuer: process.env.NODE_ENV === 'development' ? 'https://secure.uat.enterprise.telenet.be/oauth2/aus873ggs9Y6H0REY0x7' : window.issuerUrl,
    clientId: process.env.NODE_ENV === 'development' ? '0oa6845ypdGD4Kriq0x7' : window.clientId,
    redirectUri: window.location.origin + oktaCallBackUrl,
    postLogoutRedirectUri: window.location.origin,
    pkce: true,
    scopes: ['openid', 'profile', 'email'],
    storageManager: {
        token: {
            storageType: 'sessionStorage'
        }
    }
});

const JwtSetup = () => {
    const dispatch = useDispatch<ThunkDispatch<RootState, any, any>>()
    const query = useQuery();
    const me = useSelector(meSelector)
    const { search } = useLocation()
    const searchParams = useMemo(() => new URLSearchParams(search), [search])

    const setup = useCallback(async () => {
        let productId = query.get("productId")
        const activeCustomerId = searchParams.get('RelayState')
        if (!me) {
            await dispatch(fetchMe({ oktaAuth: null, activeCustomerId: activeCustomerId }))
            await dispatch(selectSite(productId))
        }
    }, [dispatch, me, query, searchParams])

    useEffect(() => {
        setup()
    }, [setup])

    return <></>
}

const OAuthSetup = () => {
    const {authState} = useOktaAuth()
    const dispatch = useDispatch<ThunkDispatch<RootState, any, any>>()
    const query = useQuery();
    const me = useSelector(meSelector)
    const { loggedOut } = useOktaSession()
    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const setup = useCallback(async () => {
        if (authState?.isAuthenticated) {
            api.defaults.headers.common['Authorization'] = "Bearer " + authState?.accessToken?.accessToken;
        }
        let productId = query.get("productId")
        const activeCustomerId = searchParams.get('RelayState')
        if (!me) {
            const me = await dispatch(fetchMe({oktaAuth: oktaAuth, activeCustomerId: activeCustomerId}))
            if (me.payload === 'UNAUTHORIZED') {
                await loggedOut()
                return
            } else if (me.payload !== '401' && me.payload !== 'PRODUCT_NOT_FOUND' && me.payload !== 'CUSTOMER_NOT_SELECTED') {
                await dispatch(selectSite(productId))
            }
        }
    }, [dispatch, me, query, authState?.accessToken, authState?.isAuthenticated, loggedOut, searchParams])

    useEffect(() => {
        setup()
    }, [setup])

    return <></>
}

const AuthenticatedRoutes = ({useOAuth}: { useOAuth: boolean }) => {
    const inEnterpriseView = useSelector(inEnterpriseViewSelector)
    const isLoading = useSelector(loadingSelector)
    const loadingFailure = useSelector(loadingFailureSelector)
    const me = useSelector(meSelector)

    const MainScreen = useMemo(() =>
        isLoading && !loadingFailure? <Loading/> : <Switch>
            <Route path='/'>
                <AppContainer>
                    <Switch>
                        <Redirect from='/' exact to={(routeDefinitions(inEnterpriseView, me).find((d): d is NavigationRoute => !('separator' in d)))?.href || ''} />,
                        <Route path={'/switch-site'} component={SiteSwitcher} key={'siteSwitcher'}/>,
                        <Route path={'/not-found'} component={NotFound} key={'notFound'} />,
                        {RouteDefinitions.map(definitionToRoute(useOAuth))},
                        <Route component={NotFound} key={'catchAll'} />
                    </Switch>
                </AppContainer>
            </Route>
        </Switch>
        , [useOAuth, inEnterpriseView, isLoading, loadingFailure, me])

    const SetupMemo = useMemo(() => useOAuth? <OAuthSetup/> : <JwtSetup/>, [useOAuth])
    return (
        <>
            {SetupMemo}
            {MainScreen}
        </>
    )
}

export const Loading = () => <Flex alignItems={'center'} justifyContent={'center'}>
    <Box style={{marginTop: '20%'}}>
        <Spinner size={36}/>
    </Box>
</Flex>

const queryClient = new QueryClient()

const OAuthSecuredApp = () => {
    const history = useHistory();
    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const triggerLogin = useCallback(async () => {
        const relayState = searchParams.get("RelayState")
        await oktaAuth.signInWithRedirect({originalUri: '/' + (!!relayState?  ('?RelayState=' + relayState) : ''), state: relayState || undefined});
    }, [searchParams]);

    const customAuthHandler = useCallback(async () => {
        const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
        if ((!previousAuthState || !previousAuthState.isAuthenticated)) {
            await triggerLogin()
        } else {
            // Ask the user to trigger the login process during token autoRenew process
            //todo
        }
    }, [triggerLogin]);

    const restoreOriginalUri = useCallback(async (_oktaAuth: OktaAuth, originalUri: string) => {
        history.replace(toRelativeUrl((originalUri || '/'), window.location.origin));
    }, [history])

    return <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri} onAuthRequired={customAuthHandler}>
        <Switch>
            <Route path='/sign-out' component={Logout}/>
            <Route path='/unauthorized' component={UnAuthorized}/>
            <Route path={oktaCallBackUrl} component={LoginCallback}/>
            <SecureRoute path='/' render={() => <AuthenticatedRoutes useOAuth={true}/>}/>
        </Switch>
    </Security>
}

const EdgeApp = () => {
    const routes = <Switch>
        <Route path='/' render={() => <AuthenticatedRoutes useOAuth={false} />} />
    </Switch>

    // Edge embeds this app in an iframe under a host-controlled path (e.g. /edgeweb/.../b/) that
    // the SPA has no route for. Routing this subtree through its own MemoryRouter, instead of the
    // outer BrowserRouter, keeps all in-app navigation off the real window.location entirely, so it
    // always starts at '/' regardless of the host path, and the gateway's Referer-based routing for
    // absolute-path static assets (which relies on window.location matching the Edge-imposed path)
    // is never disrupted by client-side navigation. Other Edge-authenticated entry points (e.g. the
    // FSPS Management UI) load this app directly at the real context root with no such mismatch, so
    // they keep the outer BrowserRouter and a real, navigable URL.
    const isEmbedded = window.self !== window.top
    if (!isEmbedded) return routes

    return <MemoryRouter initialEntries={['/']}>
        <ScrollToTop />
        {routes}
    </MemoryRouter>
}

export const App = () => {
    const [loading, setLoading] = useState<boolean>(true)
    const [loggedIn, setLoggedIn] = useState<boolean>(false)
    const [noProductFoundInEdge, setNoProductFoundInEdge] = useState<boolean>(false)
    useEffect(() => {
        const fetch = async () => {
            try {
                const {data: user} = await api.get('/api/me')
                setLoggedIn(!!user)
            } catch (err: any) {
                if (err?.response?.status === 400) {
                    setNoProductFoundInEdge(true)
                }
            }
            setLoading(false)
        }
        fetch();
    }, [])

    return noProductFoundInEdge ? <Container maxWidth="sm" sx={{ marginTop: 8 }}>
        <ErrorAlert showAlert={true} body={<>No products found</>} />
    </Container> : <QueryClientProvider client={queryClient}>
                <Router basename={basepath}>
                    <ScrollToTop/>
                    {loading && <Loading/>}
                    {!loggedIn && !loading && <OAuthSecuredApp/>}
                    {loggedIn && !loading && <EdgeApp/>}
                </Router>
            </QueryClientProvider>
}
